import { safeSetItem } from "@/lib/storage/safe-local";
// Client-side capture of marketing/attribution metadata: UTM params,
// referrer, device, browser, landing page. Persisted for the visitor's
// session so it survives the trip to checkout, and attached to the
// pending intent when checkout starts.

export type MarketingMetadata = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landing_page?: string;
  browser?: string;
  device?: string;
  screen?: string;
  language?: string;
  timezone?: string;
  first_seen_at?: string;
};

const KEY = "cb16.marketing";

function detectBrowser(ua: string): string {
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return "Safari";
  return "Other";
}
function detectDevice(ua: string): string {
  if (/iPad/.test(ua)) return "Tablet";
  if (/Mobi|Android|iPhone/.test(ua)) return "Mobile";
  return "Desktop";
}

/** Call once on landing (root layout) to snapshot attribution. */
export function initMarketingCapture(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const existing = readMarketing();
    const utms: Partial<MarketingMetadata> = {};
    (["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const).forEach(
      (k) => {
        const v = url.searchParams.get(k);
        if (v) utms[k] = v.slice(0, 120);
      },
    );
    const hasNewUtm = Object.keys(utms).length > 0;
    // Only overwrite if this visit brings new UTMs, otherwise preserve first-touch.
    if (existing && !hasNewUtm) return;
    const ua = navigator.userAgent || "";
    const payload: MarketingMetadata = {
      ...(existing ?? {}),
      ...utms,
      referrer: document.referrer ? document.referrer.slice(0, 500) : (existing?.referrer ?? ""),
      landing_page: hasNewUtm || !existing ? window.location.href.slice(0, 500) : existing.landing_page,
      browser: detectBrowser(ua),
      device: detectDevice(ua),
      screen: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      first_seen_at: existing?.first_seen_at ?? new Date().toISOString(),
    };
    safeSetItem(KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function readMarketing(): MarketingMetadata | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as MarketingMetadata) : null;
  } catch {
    return null;
  }
}

export function clearMarketing(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}